export type HttpMethod = 'GET' | 'POST' | 'DELETE'

export interface CodeSamples {
  curl: string
  python: string
}

export interface EndpointDoc {
  id: string
  navLabel: string
  title: string
  method: HttpMethod
  path: string
  description: string
  auth: string
  requestExample: string
  responseExample: string
  codeSamples: CodeSamples
}

const PROD = 'https://swaasth.supervity.ai/api/ai/recipes'

export function getApiBaseUrl(): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return `${apiUrl}${basePath}/api/ai/recipes`
}

export function getEndpointUrl(path: string): string {
  return `${getApiBaseUrl()}${path}`
}

export const DEVELOPER_API_NAV = [
  { id: 'extract-ingredients', label: 'Extract Ingredients' },
  { id: 'generate', label: 'Generate Recipes' },
  { id: 'saved-list', label: 'Get Saved Recipes' },
  { id: 'save-recipe', label: 'Save Recipe' },
  { id: 'delete-recipe', label: 'Delete Saved Recipe' },
  { id: 'history', label: 'Recipe History' },
] as const

export const ENDPOINT_DOCS: EndpointDoc[] = [
  {
    id: 'extract-ingredients',
    navLabel: 'Extract Ingredients',
    title: 'Extract Ingredients',
    method: 'POST',
    path: '/extract-ingredients',
    description: 'Accepts a food image and returns ingredient names in plain English.',
    auth: 'None required',
    requestExample: 'multipart/form-data\n\nfile: image file (jpeg, png, or webp)',
    responseExample: JSON.stringify({ ingredients: ['tomato', 'onion', 'spinach'] }, null, 2),
    codeSamples: {
      curl: `curl -X POST "${PROD}/extract-ingredients" \\\n  -F "file=@/path/to/ingredients.jpg"`,
      python: `import requests\n\nwith open('ingredients.jpg', 'rb') as f:\n    r = requests.post(\n        '${PROD}/extract-ingredients',\n        files={'file': f}\n    )\nprint(r.json())`,
    },
  },
  {
    id: 'generate',
    navLabel: 'Generate Recipes',
    title: 'Generate Recipes',
    method: 'POST',
    path: '/generate',
    description: 'Generates nutritionally optimized recipes from available ingredients.',
    auth: 'None required (optional Bearer token saves recipe history)',
    requestExample: JSON.stringify(
      { ingredients: ['tomato', 'spinach', 'lentils'], lang: 'en' },
      null,
      2
    ),
    responseExample: JSON.stringify(
      {
        recipes: [
          {
            id: 'rec-a1b2c3d4',
            name: 'Spinach Dal',
            difficulty: 'Easy',
            prep_time_mins: 10,
            cook_time_mins: 25,
            serving_size: 4,
            ingredients: [{ name: 'Spinach', quantity: '2 cups' }],
            instructions: ['Wash and soak lentils.'],
            nutrition_highlights: ['Rich in Iron'],
          },
        ],
        recipes_raw: [],
      },
      null,
      2
    ),
    codeSamples: {
      curl: `curl -X POST "${PROD}/generate" \\\n  -H "Content-Type: application/json" \\\n  -d '{"ingredients":["tomato","spinach","lentils"],"lang":"en"}'`,
      python: `import requests\n\nr = requests.post(\n    '${PROD}/generate',\n    json={'ingredients': ['tomato', 'spinach', 'lentils'], 'lang': 'en'}\n)\nprint(r.json())`,
    },
  },
  {
    id: 'saved-list',
    navLabel: 'Get Saved Recipes',
    title: 'Get Saved Recipes',
    method: 'GET',
    path: '/saved?lang=en',
    description: 'Returns bookmarked recipes for the authenticated user.',
    auth: 'Bearer token required',
    requestExample: 'No request body.\n\nQuery: lang=en | hi | mr',
    responseExample: JSON.stringify(
      [
        {
          id: 42,
          recipe_id: 'rec-a1b2c3d4',
          recipe_data: { id: 'rec-a1b2c3d4', name: 'Spinach Dal' },
          created_at: '2026-06-15T10:30:00+00:00',
        },
      ],
      null,
      2
    ),
    codeSamples: {
      curl: `curl -X GET "${PROD}/saved?lang=en" \\\n  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,
      python: `import requests\n\nr = requests.get(\n    '${PROD}/saved',\n    params={'lang': 'en'},\n    headers={'Authorization': 'Bearer YOUR_ACCESS_TOKEN'}\n)\nprint(r.json())`,
    },
  },
  {
    id: 'save-recipe',
    navLabel: 'Save Recipe',
    title: 'Save Recipe',
    method: 'POST',
    path: '/saved?lang=en',
    description: 'Bookmarks a recipe for the authenticated user.',
    auth: 'Bearer token required',
    requestExample: JSON.stringify(
      {
        recipe_id: 'rec-a1b2c3d4',
        recipe_data: { id: 'rec-a1b2c3d4', en: {}, hi: {}, mr: {} },
      },
      null,
      2
    ),
    responseExample: JSON.stringify(
      {
        id: 42,
        recipe_id: 'rec-a1b2c3d4',
        recipe_data: { id: 'rec-a1b2c3d4', name: 'Spinach Dal' },
        created_at: '2026-06-15T10:30:00+00:00',
      },
      null,
      2
    ),
    codeSamples: {
      curl: `curl -X POST "${PROD}/saved?lang=en" \\\n  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"recipe_id":"rec-a1b2c3d4","recipe_data":{...}}'`,
      python: `import requests\n\nr = requests.post(\n    '${PROD}/saved',\n    params={'lang': 'en'},\n    headers={'Authorization': 'Bearer YOUR_ACCESS_TOKEN'},\n    json={'recipe_id': 'rec-a1b2c3d4', 'recipe_data': {...}}\n)\nprint(r.json())`,
    },
  },
  {
    id: 'delete-recipe',
    navLabel: 'Delete Saved Recipe',
    title: 'Delete Saved Recipe',
    method: 'DELETE',
    path: '/saved/{recipe_id}',
    description: 'Removes a bookmarked recipe for the authenticated user.',
    auth: 'Bearer token required',
    requestExample: 'No request body.\n\nPath: recipe_id (e.g. rec-a1b2c3d4)',
    responseExample: JSON.stringify(
      { status: 'success', message: 'Recipe removed from bookmarks.' },
      null,
      2
    ),
    codeSamples: {
      curl: `curl -X DELETE "${PROD}/saved/rec-a1b2c3d4" \\\n  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,
      python: `import requests\n\nr = requests.delete(\n    '${PROD}/saved/rec-a1b2c3d4',\n    headers={'Authorization': 'Bearer YOUR_ACCESS_TOKEN'}\n)\nprint(r.json())`,
    },
  },
  {
    id: 'history',
    navLabel: 'Recipe History',
    title: 'Get Recipe History',
    method: 'GET',
    path: '/history?lang=en',
    description: 'Returns previously generated recipes for the authenticated user.',
    auth: 'Bearer token required',
    requestExample: 'No request body.\n\nQuery: lang=en | hi | mr',
    responseExample: JSON.stringify(
      [
        {
          id: 101,
          ingredients: ['tomato', 'spinach', 'lentils'],
          recipe_data: { id: 'rec-a1b2c3d4', name: 'Spinach Dal' },
          created_at: '2026-06-15T10:30:00+00:00',
        },
      ],
      null,
      2
    ),
    codeSamples: {
      curl: `curl -X GET "${PROD}/history?lang=en" \\\n  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,
      python: `import requests\n\nr = requests.get(\n    '${PROD}/history',\n    params={'lang': 'en'},\n    headers={'Authorization': 'Bearer YOUR_ACCESS_TOKEN'}\n)\nprint(r.json())`,
    },
  },
]
